import { useState } from 'react';
import { Form as FinalForm, FormSpy } from 'react-final-form';
import { Alert, Button, Form } from 'reactstrap';

import FormFieldSwitchPill from '../../../../../components/formFields/FormFieldSwitchPill';
import FormFieldTextOrNumber from '../../../../../components/formFields/FormFieldTextOrNumber';
import FormFieldWithDropdown from '../../../../../components/formFields/FormFieldWithDropdown';
import { createGoogleMerchantDataSourceDisplayName, createGoogleMerchantProfileName, currencyOptions } from '../../utilities';

export function ProfileCard
(
    {
        profile,
        merchantAccountId,
        onUpdateProfile,
        onDeleteProfile,
        onCreateDataSourceForProfile
    }
    : any
)
{
    const initialGeneratedName = createGoogleMerchantProfileName(profile);

    const [message, setMessage] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isCreatingDataSource, setIsCreatingDataSource] = useState(false);
    const [isNameManuallyEdited, setIsNameManuallyEdited] = useState(!!profile.name && profile.name !== initialGeneratedName);

    const handleSubmit = async (values: any) =>
    {
        setMessage(null);
        setError(null);

        try
        {
            await onUpdateProfile(profile.profileId, {
                ...profile,
                ...values,
                profileId: profile.profileId
            });

            setMessage('Profil został zapisany.');
        }
        catch (exception: any)
        {
            setError(exception?.message ?? 'Zapis profilu zakończył się błędem.');
        }
    };

    const handleDelete = async () =>
    {
        setMessage(null);
        setError(null);
        setIsDeleting(true);

        try
        {
            await onDeleteProfile(profile.profileId);
        }
        catch (exception: any)
        {
            setError(exception?.message ?? 'Usunięcie profilu zakończyło się błędem.');
        }
        finally
        {
            setIsDeleting(false);
        }
    };

    const handleCreateDataSource = async () =>
    {
        setMessage(null);
        setError(null);
        setIsCreatingDataSource(true);

        try
        {
            await onCreateDataSourceForProfile(profile.profileId);
            setMessage('Źródło danych zostało utworzone.');
        }
        catch (exception: any)
        {
            setError(exception?.message ?? 'Tworzenie źródła danych zakończyło się błędem.');
        }
        finally
        {
            setIsCreatingDataSource(false);
        }
    };

    return (
        <FinalForm
            initialValues={profile}
            onSubmit={handleSubmit}
            subscription={{ values: true, dirty: true, submitting: true }}
            render={({ handleSubmit, values, dirty, form }) =>
            {
                const currency = currencyOptions.find(option => option.id === values.currencyCode);
                const profileId = values.profileId || profile.profileId;
                const isEnabled = values.enabled !== false;
                const hasDataSource = !!values.dataSourceName;
                const hasLanguageBinding = values.isActiveInStore === true && !!values.languageName;

                const missingItems =
                [
                    !merchantAccountId ? 'Merchant Account ID' : null,
                    !profileId ? 'ID profilu' : null,
                    !values.dataSourceName ? 'źródła danych' : null,
                    !values.contentLanguage ? 'content language' : null,
                    !values.feedLabel ? 'feed label' : null,
                    !values.currencyCode ? 'waluty' : null,
                    values.isActiveInStore === false ? `aktywnego języka sklepu dla ${values.contentLanguage || 'profilu'}` : null,
                    values.isActiveInStore !== false && !values.languageName ? 'powiązania z językiem sklepu' : null
                ].filter(Boolean);

                const canCreateDataSource =
                    !!merchantAccountId &&
                    !!profile.profileId &&
                    !dirty &&
                    !values.dataSourceName;

                const isReadyForExport =
                    isEnabled &&
                    hasDataSource &&
                    !!values.contentLanguage &&
                    !!values.feedLabel &&
                    !!values.currencyCode &&
                    hasLanguageBinding;

                return (
                    <Form onSubmit={handleSubmit} className="border rounded p-3 mb-3">
                        <FormSpy
                            subscription={{ values: true }}
                            onChange={({ values }) =>
                            {
                                if (!isNameManuallyEdited)
                                {
                                    const generatedName = createGoogleMerchantProfileName(values);

                                    if (values.name !== generatedName)
                                    {
                                        form.change('name', generatedName);
                                    }
                                }

                                if (!values.dataSourceDisplayName)
                                {
                                    const generatedDataSourceDisplayName = createGoogleMerchantDataSourceDisplayName(values);

                                    if (generatedDataSourceDisplayName)
                                    {
                                        form.change('dataSourceDisplayName', generatedDataSourceDisplayName);
                                    }
                                }
                            }}
                        />
                        <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center mb-3">
                            <div>
                                <strong>{values.name || 'Profil eksportu'}</strong>
                                {isReadyForExport && <span className="badge badge-success ml-2">gotowe do eksportu</span>}
                                {!isReadyForExport && hasDataSource && <span className="badge badge-info ml-2">źródło skonfigurowane</span>}
                                {!isEnabled && <span className="badge badge-secondary ml-2">profil wyłączony</span>}
                                {dirty && <span className="badge badge-warning ml-2">zmieniony</span>}
                                {values.isActiveInStore === false && <span className="badge badge-warning ml-2">język nieaktywny w sklepie</span>}
                                {values.isActiveInStore !== false && !values.languageName && <span className="badge badge-warning ml-2">brak powiązania z językiem</span>}
                                {/* <div className="text-muted small mt-1">
                                    <div>{profileId || 'brak'}</div>
                                </div> */}
                            </div>

                            {missingItems.length > 0 && <Alert color="warning" className="mb-0">Brakuje: {missingItems.join(', ')}.</Alert>}

                            <div className="mt-3 mt-lg-0 d-flex align-items-center">
                                <Button
                                    type="submit"
                                    color="primary"
                                    size="md"
                                    className="mr-2"
                                    disabled={!dirty}
                                >
                                    Zapisz
                                </Button>

                                <Button
                                    type="button"
                                    color="secondary"
                                    size="md"
                                    className="mr-2"
                                    disabled={!canCreateDataSource || isCreatingDataSource}
                                    onClick={handleCreateDataSource}
                                >
                                    Utwórz źródło
                                </Button>

                                <Button
                                    type="button"
                                    color="link"
                                    size="md"
                                    className="text-danger p-0"
                                    disabled={isDeleting}
                                    onClick={handleDelete}
                                >
                                    Usuń
                                </Button>
                            </div>
                        </div>

                        {message && <Alert color="success">{message}</Alert>}
                        {error && <Alert color="danger">{error}</Alert>}

                        {dirty && (
                            <Alert color="warning">
                                Ten profil ma niezapisane zmiany. Utworzenie źródła danych będzie możliwe po zapisaniu profilu.
                            </Alert>
                        )}

                        <div className="row">
                            <div className="col-12 col-xl-4">
                                <div className="mb-3">
                                    <FormFieldTextOrNumber
                                        name="name"
                                        label="Nazwa profilu"
                                        onChange={(inputOnChange: any) => (event: any) =>
                                        {
                                            setIsNameManuallyEdited(true);
                                            inputOnChange(event);
                                        }}
                                    />
                                </div>

                                <div className="mb-3">
                                    <FormFieldTextOrNumber name="contentLanguage" label="Content language" />
                                </div>

                                <div className="mb-3">
                                    <FormFieldTextOrNumber name="feedLabel" label="Feed label" />
                                </div>
                            </div>

                            <div className="col-12 col-xl-4">
                                <div className="mb-3">
                                    <FormFieldWithDropdown
                                        name="currencyCode"
                                        label="Waluta"
                                        options={currencyOptions}
                                        currentSelectionLabel={currency?.label ?? values.currencyCode ?? 'Wybierz'}
                                    />
                                </div>

                                <div className="mb-3">
                                    <FormFieldTextOrNumber
                                        name="dataSourceDisplayName"
                                        label="Nazwa źródła danych"
                                    />
                                </div>

                                <div className="mb-3">
                                    <FormFieldSwitchPill
                                        name="enabled"
                                        label="Aktywny"
                                        className=""
                                        thin
                                    />
                                </div>
                            </div>

                            <div className="col-12 col-xl-4">
                                <div className="mb-3">
                                    <FormFieldTextOrNumber
                                        name="dataSourceName"
                                        label="Data source resource name"
                                    />
                                    <small className="text-muted">
                                        Np. accounts/{merchantAccountId || '5810261935'}/dataSources/123456789.
                                    </small>
                                </div>

                                <div className="mb-3">
                                    <FormFieldTextOrNumber
                                        name="sortOrder"
                                        label="Kolejność"
                                        type="number"
                                    />
                                </div>

                                <div className="mb-3">
                                    <FormFieldTextOrNumber
                                        name="profileId"
                                        label="ID profilu"
                                        readOnly
                                    />
                                    {/* <small className="text-muted">
                                        Ten identyfikator musi zgadzać się z profileId w stanach eksportu.
                                    </small> */}
                                </div>

                                <div className="mb-3">
                                    <FormFieldTextOrNumber
                                        name="languageName"
                                        label="Język sklepu"
                                        readOnly
                                    />
                                    <small className="text-muted">
                                        Puste pole oznacza, że profil nie został powiązany z aktywnym językiem sklepu.
                                    </small>
                                </div>
                            </div>
                        </div>
                    </Form>
                );
            }}
        />
    );
}